import { Link, NavLink, useLocation } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useClientView } from "../context/ClientViewProvider";
import { useLocale } from "../../intl";
import { useLocalizedRuntimePaths } from "../hooks/useLocalizedRuntimePaths";
import AutoMapaLocaleControls from "./AutoMapaLocaleControls";
import AutoMapaLogo from "./AutoMapaLogo";
import "../styles/automapHomeNav.css";

const MOBILE_NAV_MEDIA_QUERY = "(max-width: 980px)";

const NAV_ITEMS = [
  { id: "trust", labelKey: "automotive.nav.trust", pathKey: "buyerQuizPath" },
  { id: "inventory", labelKey: "automotive.homeNavInventory", pathKey: "vehicleSearchPath" },
  { id: "dealers", labelKey: "automotive.nav.dealers", pathKey: "dealerDirectoryPath" },
  { id: "howItWorks", labelKey: "automotive.nav.howItWorks", pathKey: "howItWorksPath" },
];

function isNavItemActive(pathname, href) {
  if (!href) return false;
  const normalizedPath = pathname.replace(/\/$/, "") || "/";
  const normalizedHref = href.replace(/\/$/, "") || "/";
  return (
    normalizedPath === normalizedHref ||
    normalizedPath.startsWith(`${normalizedHref}/`)
  );
}

function useMobileNavLayout() {
  const [isMobileNav, setIsMobileNav] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(MOBILE_NAV_MEDIA_QUERY).matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_NAV_MEDIA_QUERY);
    const handleChange = () => setIsMobileNav(mediaQuery.matches);
    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return isMobileNav;
}

export default function AutoMapaHomeNav({ siteName: siteNameProp }) {
  const { manifest } = useClientView();
  const siteName = siteNameProp || manifest?.name || "AutoMapa";
  const { t } = useLocale();
  const paths = useLocalizedRuntimePaths();
  const { homePath } = paths;
  const location = useLocation();
  const isMobileNav = useMobileNavLayout();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isHomePage = useMemo(() => {
    const normalizedHome = homePath.replace(/\/$/, "");
    const normalizedPath = location.pathname.replace(/\/$/, "");
    return normalizedPath === normalizedHome;
  }, [homePath, location.pathname]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileNav) {
      setMobileMenuOpen(false);
    }
  }, [isMobileNav]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const desktopSectionLinks = useMemo(
    () =>
      NAV_ITEMS.map(({ id, labelKey, pathKey }) => {
        const label = t(labelKey);
        const to = paths[pathKey];
        return (
          <NavLink
            key={id}
            to={to}
            className={({ isActive }) => (isActive ? "is-active" : undefined)}
          >
            {label}
          </NavLink>
        );
      }),
    [paths, t]
  );

  const mobileSectionLinks = useMemo(
    () =>
      NAV_ITEMS.map(({ id, labelKey, pathKey }) => {
        const label = t(labelKey);
        const to = paths[pathKey];
        const isActive = isNavItemActive(location.pathname, to);
        return (
          <Link
            key={id}
            to={to}
            className={`automap-home-nav__mobile-link${isActive ? " is-active" : ""}`}
            onClick={closeMobileMenu}
          >
            {label}
          </Link>
        );
      }),
    [closeMobileMenu, location.pathname, paths, t]
  );

  const brand = isHomePage ? (
    <a className="brand automap-home-nav__brand" href="#top" aria-label={siteName}>
      <AutoMapaLogo variant="on-dark" />
    </a>
  ) : (
    <Link className="brand automap-home-nav__brand" to={homePath} aria-label={siteName}>
      <AutoMapaLogo variant="on-dark" />
    </Link>
  );

  return (
    <>
      <nav
        className={`nav automap-home-nav${mobileMenuOpen ? " automap-home-nav--menu-open" : ""}`}
      >
        <div className="wrap nav-inner">
          {brand}
          {!isMobileNav ? (
            <>
              <div className="links automap-home-nav__links--desktop" aria-label={t("automotive.nav.mainAria")}>
                {desktopSectionLinks}
              </div>
              <AutoMapaLocaleControls
                className="nav-right automap-home-nav__controls--desktop"
                showSignIn
              />
            </>
          ) : (
            <button
              type="button"
              className="automap-home-nav__menu-toggle"
              aria-expanded={mobileMenuOpen}
              aria-controls="automap-home-nav-mobile-panel"
              aria-label={mobileMenuOpen ? t("common.close") : t("automotive.nav.openMenu")}
              onClick={() => setMobileMenuOpen((open) => !open)}
            >
              <span
                className={`automap-home-nav__menu-icon${mobileMenuOpen ? " is-open" : ""}`}
                aria-hidden="true"
              >
                <span />
                <span />
                <span />
              </span>
            </button>
          )}
        </div>
      </nav>

      {isMobileNav && mobileMenuOpen ? (
        <button
          type="button"
          className="automap-home-nav__backdrop"
          aria-label={t("common.close")}
          onClick={closeMobileMenu}
        />
      ) : null}

      {isMobileNav && mobileMenuOpen ? (
        <div
          id="automap-home-nav-mobile-panel"
          className="automap-home-nav__mobile-panel is-open"
        >
          <div className="wrap automap-home-nav__mobile-panel-inner">
            <nav className="automap-home-nav__mobile-links" aria-label={t("automotive.nav.mainAria")}>
              {mobileSectionLinks}
            </nav>
            <AutoMapaLocaleControls className="automap-home-nav__mobile-controls" showSignIn />
          </div>
        </div>
      ) : null}
    </>
  );
}
